import { LitElement, html, TemplateResult, PropertyValues } from "lit";
import { customElement, property, query } from "lit/decorators.js";
import { IconDefaultType as IconType } from "@slhuiicon/icon";
import { SlhCoreChip } from "@slhuicore/chip";

import { ClassInfo } from "lit/directives/class-map";
import { style } from "./assets/chip.generated-style";

export const CHIP_EVENT_DISMISS = "dismiss";

/**
 * Allow users to categorize content using short textual information, make selections or filter content.
 * They should appear dynamically as a group of multiple interactive elements.
 *
 * @CustomElement slh-chip
 *
 * @cssprop --slh-color-neutral-shade-60 - chip font color
 * @cssprop --slh-color-neutral-shade-80 - chip hover font color
 * @cssprop --slh-color-neutral-shade-00 -  chip background color
 * @cssprop --slh-color-neutral-shade-20 - chip border color
 * @cssprop --slh-color-neutral-shade-00 - chip selected font color
 * @cssprop --slh-color-neutral-shade-40 - chip disabled icon color
 * @cssprop --slh-color-neutral-shade-40 - chip disabled font color
 * @cssprop --slh-color-neutral-shade-10 - chip disabled background color
 * @cssprop -slh-default-font-family -  font family
 * @cssprop --slh-base-xs-font-size - font size
 * @cssprop --slh-base-semi-bold-font-weight - font weight
 * @cssprop --slh-base-xxs-line-height - line height
 * @cssprop --slh-base-xxs-font-size - small chip font size
 *
 * ** slots
 * @slot default - Default slot
 *
 */
@customElement("slh-chip")
export default class SlhChip extends SlhCoreChip(LitElement) {
  /**
   * adds smaller font size
   */
  @property({
    type: Boolean,
  })
  small: Boolean = false;

  /**
   * adds color to the chip
   */
  @property({
    type: String,
  })
  color: string = "";

  /**
   * adds primary styling to the chip
   */
  @property({
    type: Boolean,
  })
  primary: boolean = false;

  /**
   * adds success styling to the chip
   */
  @property({
    type: Boolean,
  })
  success: boolean = false;

  /**
   * adds info styling to the chip
   */
  @property({
    type: Boolean,
  })
  info: boolean = false;

  /**
   * adds warning styling to the chip
   */
  @property({
    type: Boolean,
  })
  warning: boolean = false;

  /**
   * adds error styling to the chip
   */
  @property({
    type: Boolean,
  })
  error: boolean = false;

  /**
   * adds color to the chip
   */
  @property({
    type: String,
    attribute: "border-color",
  })
  borderColor: string = "";

  @query(".slh-chip", false)
  private chipContainer!: HTMLElement;

  static styles = style;

  protected firstUpdated(changedProperties: PropertyValues) {
    super.firstUpdated(changedProperties);
    if (this.color && this.borderColor) {
      this.small = true;
      this.selected = false;
      this.disabled = false;
      this.dismissible = false;
      this.chipContainer.style.color = this.color;
      this.chipContainer.style.borderColor = this.borderColor;
      this.chipContainer.classList.add("colored");
    }
    if (this.small) {
      this.chipContainer.classList.add("small");
    }
  }

  /**
   * @returns {ClassInfo}
   */
  private renderClasses(): ClassInfo {
    return {
      selected: this.selected,
      disabled: this.disabled,
      dismissible: this.dismissible,
      primary: this.primary,
      success: this.success,
      info: this.info,
      warning: this.warning,
      error: this.error,
    };
  }

  /**
   * @returns {TemplateResult}
   */
  // eslint-disable-next-line class-methods-use-this
  renderIconTemplate(): TemplateResult {
    return html`<slh-icon type="${IconType.ICO_SMALL_CLOSE}"></slh-icon>`;
  }
}
